import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col } from "@/Components/ui";
import { Upload, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { API, BACKEND_HOST } from "../../../api";
import { dialogService } from "@/utils/dialogService";
interface AmenityItem {
    id?: number;
    name: string;
    image?: string | null;
    description: string;
    created_at?: string;
}
interface AmenityModalProps {
    isOpen: boolean;
    toggle: () => void;
    amenity: AmenityItem | null;
    onSave: (amenity: AmenityItem) => void;
    isCopyMode?: boolean;
}
const AmenityModal: React.FC<AmenityModalProps> = ({ isOpen, toggle, amenity, onSave, isCopyMode = false, }) => {
    const [formData, setFormData] = useState<AmenityItem>({
        name: "",
        description: "",
        image: null,
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    useEffect(() => {
        if (amenity) {
            setFormData({
                ...amenity,
                name: isCopyMode ? amenity.name + " (복사)" : amenity.name,
            });
            if (!isCopyMode) {
                setImagePreview(amenity.image || null);
            }
            else {
                setImagePreview(null);
            }
        }
        else {
            setFormData({
                name: "",
                description: "",
                image: null,
            });
            setImagePreview(null);
        }
        setImageFile(null);
    }, [amenity, isOpen, isCopyMode]);
    useEffect(() => {
        return () => {
            if (imagePreview && imageFile) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview, imageFile]);
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };
    const handleDeleteImage = async () => {
        if (!await dialogService.confirm("이미지를 삭제하시겠습니까?"))
            return;
        setImagePreview(null);
        setImageFile(null);
        setFormData(prev => ({ ...prev, image: null }));
    };
    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error("편의시설 이름을 입력해주세요.");
            return;
        }
        setIsSaving(true);
        try {
            const data = new FormData();
            data.append("name", formData.name);
            data.append("description", formData.description);
            if (imageFile) {
                data.append("image", imageFile);
            }
            else if (imagePreview === null && amenity?.id && !isCopyMode) {
                // Delete existing image
                data.append("image", "");
            }
            let response;
            if (amenity?.id && !isCopyMode) {
                response = await API.patch(`${BACKEND_HOST}/sites/amenity-items/${amenity.id}/`, data);
            }
            else {
                response = await API.post(`${BACKEND_HOST}/sites/amenity-items/`, data);
            }
            onSave(response.data);
            toast.success("편의시설 정보가 저장되었습니다.");
            toggle();
        }
        catch (error: any) {
            console.error("Save amenity error:", error);
            let errorMessage = "편의시설 저장 중 오류가 발생했습니다.";
            if (error.response?.data) {
                const data = error.response.data;
                if (typeof data === 'object') {
                    const messages = Object.keys(data).map(key => {
                        const val = data[key];
                        return `${key}: ${Array.isArray(val) ? val.join(', ') : val}`;
                    });
                    if (messages.length > 0)
                        errorMessage = messages.join('\n');
                }
                else if (typeof data === 'string' && data.length < 100) {
                    errorMessage = data;
                }
            }
            toast.error(errorMessage);
        }
        finally {
            setIsSaving(false);
        }
    };
    return (<Modal isOpen={isOpen} onClose={toggle} size="xl" title={amenity && !isCopyMode ? "편의시설 수정" : (isCopyMode ? "편의시설 복사 등록" : "새 편의시설 등록")}>
      <ModalBody>
        <Form>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="amenity-name">
                  편의시설 이름 <span className="text-danger">*</span>
                </Label>
                <Input type="text" id="amenity-name" name="name" value={formData.name} onChange={handleInputChange} placeholder="예: 와이파이, 주차장, 샤워실"/>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="amenity-description">설명</Label>
                <Input type="textarea" id="amenity-description" name="description" {...({ rows: 6 } as any)} value={formData.description} onChange={handleInputChange} placeholder="편의시설에 대한 간단한 설명을 입력하세요."/>
              </FormGroup>
            </Col>

            <Col md={6}>
              <Label>아이콘 이미지</Label>
              <div className="mb-3 border-2 border-dashed border-gray-200 rounded-lg overflow-hidden h-64 flex flex-col items-center justify-center bg-gray-50 relative group">
                {imagePreview ? (<>
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-contain p-4"/>
                    <div className="absolute top-2 right-2 flex gap-2">
                      <Button color="light" size="sm" className="shadow-sm py-1 h-auto" onClick={() => document.getElementById("amenity-image")?.click()}>
                        변경
                      </Button>
                      <Button color="danger" size="sm" className="shadow-sm p-1.5 h-auto text-white bg-red-500 hover:bg-red-600 border-0" onClick={handleDeleteImage}>
                        <Trash2 className="w-4 h-4"/>
                      </Button>
                    </div>
                  </>) : (<div className="flex flex-col items-center cursor-pointer" onClick={() => document.getElementById("amenity-image")?.click()}>
                    <Upload size={40} className="text-gray-400 mb-3"/>
                    <span className="text-sm text-gray-500">이미지 업로드</span>
                    <span className="text-xs text-gray-400 mt-1">아이콘으로 사용될 이미지</span>
                  </div>)}
                <input type="file" id="amenity-image" className="hidden" accept="image/*" onChange={handleImageChange}/>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                * 투명 배경의 PNG 파일을 권장합니다. (정사각형 비율)
              </p>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={isSaving}>
          취소
        </Button>
        <Button color="success" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "저장 중..." : "저장하기"}
        </Button>
      </ModalFooter>
    </Modal>);
};
export default AmenityModal;
